import { Preorder } from "@interfaces/dws/preorder";

/** Saved coupon cents are the rounded amount, not the coupon template. Mirrors {@code PercentDiscountCents}. */
export const COUPON_DISCOUNT_CENTS_RESOLVED = "couponDiscountCentsResolved";
/** Percent coupon applies to the subtotal after the additional discount. */
export const COUPON_APPLIES_AFTER_MANUAL = "couponAppliesAfterManualDiscount";

/** Percent of integer cents, half-up. Third decimal digit >= 5 rounds up. No float. */
export function percentOfCents(baseCents: number, percent: number): number {
  const base = Math.trunc(Number(baseCents) || 0);
  const pct = Math.trunc(Number(percent) || 0);
  if (base <= 0 || pct <= 0) {
    return 0;
  }
  return Math.trunc((base * pct + 50) / 100);
}

export function couponAppliesAfterManual(data: any): boolean {
  return isTruthyFlag(data, COUPON_APPLIES_AFTER_MANUAL);
}

export function couponDiscountCentsResolved(data: any): boolean {
  return isTruthyFlag(data, COUPON_DISCOUNT_CENTS_RESOLVED);
}

/** Cents saved on the order when the server has already rounded the percent coupon. */
export function resolvedCouponDiscountCents(order: {
  data?: any;
  couponDiscountCents?: number | null;
} | null | undefined): number | null {
  if (!order || !couponDiscountCentsResolved(order.data)) {
    return null;
  }
  const cents = Math.trunc(Number(order.couponDiscountCents) || 0);
  return cents > 0 ? cents : null;
}

export function payableCents(
  subtotalCents: number,
  shipmentCents: number,
  manualDiscountCents: number,
  couponDiscountCents: number
): number {
  const subtotal = Math.trunc(Number(subtotalCents) || 0);
  const shipment = Math.trunc(Number(shipmentCents) || 0);
  const manual = Math.trunc(Number(manualDiscountCents) || 0);
  const coupon = Math.trunc(Number(couponDiscountCents) || 0);
  return Math.max(subtotal + shipment - manual - coupon, 0);
}

function isTruthyFlag(data: any, key: string): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }
  const value = data[key];
  if (value === true || value === 1) {
    return true;
  }
  return typeof value === "string" && value.trim().toLowerCase() === "true";
}

/** Mirrors {@code OrderService.calculateTotalCouponDiscountOrder}. */
export function orderCouponDiscountCents(order: {
  amountCents?: number | null;
  couponType?: string | null;
  couponDiscountCents?: number | null;
  couponDiscountPercent?: number | null;
  data?: any;
}): number {
  const resolved = resolvedCouponDiscountCents(order);
  if (resolved != null) {
    return resolved;
  }
  if (order.couponType === "absolute") {
    return order.couponDiscountCents ?? 0;
  }
  if (order.couponType === "percent") {
    if (order.couponDiscountPercent == null || order.amountCents == null) {
      return 0;
    }
    return percentOfCents(order.amountCents, order.couponDiscountPercent);
  }
  return 0;
}

export function orderManualDiscountCents(order: { amountDiscountCents?: number | null }): number {
  return order.amountDiscountCents ?? 0;
}

export function orderTotalDiscountCents(order: {
  amountCents?: number | null;
  couponType?: string | null;
  couponDiscountCents?: number | null;
  couponDiscountPercent?: number | null;
  amountDiscountCents?: number | null;
}): number {
  return orderCouponDiscountCents(order) + orderManualDiscountCents(order);
}

/**
 * Net payable in cents (WS-3252 / WS-4242).
 * Prefers {@code order_totals.total_cents} unless it looks stale (gross-only or
 * disagrees with subtotal − discounts + shipping + excise from persisted fields).
 */
export function computeOrderPayableCents(order: {
  totalCents?: number | null;
  amountCents?: number | null;
  subtotalCents?: number | null;
  couponType?: string | null;
  couponDiscountCents?: number | null;
  couponDiscountPercent?: number | null;
  amountDiscountCents?: number | null;
  amountShipmentCents?: number | null;
  amountExciseDutyCents?: number | null;
  data?: any;
}): number {
  const recomputed = recomputeOrderPayableCents(order);
  if (order.totalCents == null || order.totalCents < 0) {
    return recomputed;
  }
  const stored = Math.trunc(Number(order.totalCents));
  const amountCents = Math.trunc(Number(order.amountCents) || 0);
  const subtotalCents =
    order.subtotalCents != null && Number(order.subtotalCents) > 0
      ? Math.trunc(Number(order.subtotalCents))
      : null;
  const discount =
    orderCouponDiscountCents({
      ...order,
      amountCents: subtotalCents != null && subtotalCents > amountCents ? subtotalCents : amountCents,
    }) + orderManualDiscountCents(order);
  // POS: amountCents == totalCents while subtotalCents is gross — trust stored total.
  const amountIsPaidNet =
    stored === amountCents && subtotalCents != null && subtotalCents > amountCents;
  if (amountIsPaidNet) {
    return stored;
  }
  const looksLikeGrossOnly =
    stored === amountCents &&
    discount > 0 &&
    (subtotalCents == null || subtotalCents === amountCents);
  if (looksLikeGrossOnly || (discount > 0 && stored !== recomputed)) {
    return recomputed;
  }
  return stored;
}

export function computeOrderPayableInCurrency(
  order: Parameters<typeof computeOrderPayableCents>[0],
): number {
  return computeOrderPayableCents(order) / 100;
}

/** Self-order payable fallback — mirrors JDBC list SQL and preorders UI. */
export function computePreorderPayableCents(preorder: Preorder): number {
  if (preorder.payableTotalCents != null && preorder.payableTotalCents !== undefined) {
    return preorder.payableTotalCents;
  }
  const subtotal = preorder.total ?? 0;
  const discountTotal =
    percentOfCents(subtotal, preorder.couponDiscountPercent ?? 0) + (preorder.couponDiscountCents ?? 0);
  const shipping = preorder.extraData?.shippingData?.shippingCostCents ?? 0;
  const excise = preorder.extraData?.shippingData?.shippingFee ?? 0;
  return Math.max(subtotal - discountTotal + shipping + excise, 0);
}

/**
 * Largest-remainder (Hamilton) allocation of cents across weighted lines.
 * Mirrors {@code LargestRemainderCentsAllocator.allocate}.
 */
export function largestRemainderCentsAllocate(totalCents: number, weights: number[]): number[] {
  const w = weights || [];
  const shares = new Array(w.length).fill(0);
  const total = Math.max(0, Math.trunc(Number(totalCents) || 0));
  if (total <= 0 || w.length === 0) {
    return shares;
  }

  let totalWeight = 0;
  for (const weight of w) {
    if (weight > 0) {
      totalWeight += weight;
    }
  }
  if (totalWeight <= 0) {
    return shares;
  }

  const remainders = new Array(w.length).fill(0);
  let allocated = 0;
  for (let i = 0; i < w.length; i++) {
    const weight = w[i];
    if (weight <= 0) {
      continue;
    }
    const ideal = total * weight;
    const floor = Math.floor(ideal / totalWeight);
    remainders[i] = ideal % totalWeight;
    shares[i] = floor;
    allocated += floor;
  }

  let missing = total - allocated;
  if (missing <= 0) {
    return shares;
  }

  const indices = w.map((_, i) => i).sort((a, b) => {
    const byRemainder = remainders[b] - remainders[a];
    if (byRemainder !== 0) {
      return byRemainder;
    }
    return a - b;
  });

  for (const index of indices) {
    if (missing <= 0) {
      break;
    }
    if (w[index] <= 0) {
      continue;
    }
    shares[index]++;
    missing--;
  }

  return shares;
}

/**
 * Allocates cart-level discount (coupon + manual) on net-after-row-discount weights.
 * Mirrors {@code LargestRemainderCentsAllocator.allocateOnNetAfterRowDiscount}.
 */
export function allocateCartDiscountCents(
  cartDiscountCents: number,
  lineGrossCents: number[],
  lineRowDiscountCents?: number[] | null,
): number[] {
  const n = lineGrossCents?.length ?? 0;
  if (n === 0) {
    return [];
  }
  const weights = new Array(n);
  for (let i = 0; i < n; i++) {
    const gross = Math.max(0, Math.trunc(Number(lineGrossCents[i]) || 0));
    const rowDisc =
      lineRowDiscountCents != null && i < lineRowDiscountCents.length
        ? Math.max(0, Math.trunc(Number(lineRowDiscountCents[i]) || 0))
        : 0;
    weights[i] = Math.max(0, gross - rowDisc);
  }
  return largestRemainderCentsAllocate(cartDiscountCents, weights);
}

/**
 * Recomputes net payable from persisted discount fields (ignores {@code totalCents}).
 * Gross base prefers {@code subtotalCents} when it is the list/POS gross.
 */
export function recomputeOrderPayableCents(order: {
  totalCents?: number | null;
  amountCents?: number | null;
  subtotalCents?: number | null;
  couponType?: string | null;
  couponDiscountCents?: number | null;
  couponDiscountPercent?: number | null;
  amountDiscountCents?: number | null;
  amountShipmentCents?: number | null;
  amountExciseDutyCents?: number | null;
  data?: any;
}): number {
  const amountCents = Math.trunc(Number(order.amountCents) || 0);
  const subtotalCents =
    order.subtotalCents != null && Number(order.subtotalCents) > 0
      ? Math.trunc(Number(order.subtotalCents))
      : null;
  const storedTotal =
    order.totalCents != null && order.totalCents >= 0 ? Math.trunc(Number(order.totalCents)) : null;
  // POS: amountCents overwritten to paid net (== totalCents) while subtotalCents stays gross.
  const amountIsPaidNet =
    storedTotal != null &&
    storedTotal === amountCents &&
    subtotalCents != null &&
    subtotalCents > amountCents;
  const grossBase = amountIsPaidNet
    ? subtotalCents!
    : subtotalCents != null && subtotalCents > 0
    ? subtotalCents
    : amountCents;
  const couponDiscount = orderCouponDiscountCents({
    ...order,
    amountCents: grossBase,
  });
  const manual = orderManualDiscountCents(order);
  const shipping = Math.trunc(Number(order.amountShipmentCents) || 0);
  const excise = Math.trunc(Number(order.amountExciseDutyCents) || 0);
  return Math.max(grossBase + shipping + excise - couponDiscount - manual, 0);
}
